/**
 * Transport-agnostic backend core: composition root of all services plus the request router.
 *
 * Every transport (in-process, HTTP, WebSocket) funnels raw JSON envelopes into `handle(ctx, raw)`:
 * envelope/version check -> type check -> payload validation -> rate limit -> authentication/ban check ->
 * idempotency cache (mutations) -> service call -> response envelope. Events go out through the EventHub.
 *
 * Idempotency cache: the response of a mutation is kept per (device session, type, request id) for
 * config.idempotency.ttlMs, so a retry under the same id gets the same response without a second execution. The entry
 * remembers a hash of the payload: the same id with another payload is BAD_REQUEST 'id_reused'. The sign-in mutations
 * (auth.guest, auth.signUp, auth.signIn, auth.register, auth.login) are kept too, under a key only the retrying client can produce (see
 * idempotencyKey); a replayed sign-in binds its connection like the first one. INTERNAL, UNAVAILABLE and RATE_LIMITED
 * responses are not kept (the request did not run or failed transiently: the retry runs again).
 *
 * Across restarts (config.idempotency.persist, on for the server): every successful reply of a mutation is also stored
 * in the collection `idempotency` (written while the reply goes out; a graceful stop waits for the write), and loaded
 * again at start, so a retry after a restart gets the
 * same reply (the same account, room, replay...) instead of running the mutation twice. The document id is a keyed
 * hash (AUTH_SECRET) of the idempotency key and the reply is sealed (../../shared/crypto/seal.ts) under another keyed
 * hash of it: the data directory holds no request id and no usable token (sign-in replies carry tokens), and only a
 * retry of the same request can open it. Refused requests are not stored (they changed nothing; after a restart the
 * retry runs again). Not stored either (NOT_PERSISTED): player.command, idempotent across restarts through its opId
 * journal written atomically with the change (services/commands.ts); profile.save / profile.importLegacy (their
 * baseRevision makes a retry a CONFLICT); session.input and session.hash (a retried batch or checkpoint is recognized
 * by its ticks). auth.refresh (no mutation here) is retried through its session record (services/auth.ts). The reply
 * is stored in its own write after the mutation's: a crash between the two writes (a few milliseconds) can still let
 * a retry run the mutation again. The reply is not held back for that write: the events of the mutation are already
 * out, and a client must not see its reply lag behind them.
 *
 * Both stores are bounded by count (config.idempotency.maxEntries) and by the size of the replies they hold
 * (config.idempotency.maxBytes, JSON / sealed characters): beyond either the oldest entries go first. Without the byte
 * bound one player repeating a mutation with a large reply (a full game state, a battle init) could make the server
 * hold maxEntries of them in memory and on disk for the whole retry window.
 */
import { type Clock, systemClock } from '../../shared/clock';
import { type RandomSource, cryptoRandomSource, randomId } from '../../shared/random';
import { sha256Hex } from '../../shared/crypto/sha256';
import { wellFormedText } from '../../shared/json';
import { openSealedJson, sealJson } from '../../shared/crypto/seal';
import { type ResponseEnvelope, correlationIdOf, errorResponse, okResponse, parseRequestEnvelope, withResponseMeta } from '../../shared/protocol/envelope';
import { describeApi } from '../../shared/protocol/describe';
import { BackendError, PROTOCOL_ERROR_REASONS, type ProtocolError } from '../../shared/protocol/errors';
import {
  ALL_FEATURES,
  type ApiDescription,
  type FeatureFlag,
  type HelloResult,
  type PayloadOf,
  REQUEST_META,
  type RequestType,
  type ResultOf,
  featureOf,
  isRequestType,
} from '../../shared/protocol/messages';
import { type AuthFailureReason, type AuthResult, type PlayerId, WS_CLOSE_CODES } from '../../shared/protocol/model';
import { LIMITS, LIST_LIMITS, validatePayload } from '../../shared/protocol/validators';
import { SUPPORTED_PROTOCOL_VERSIONS, isClientTooOld, negotiateProtocolVersion, PROTOCOL_VERSION } from '../../shared/protocol/version';
import type { BattleSimulator } from './battle-sim';
import { addressLimitKey } from './client-address';
import { type BackendConfig, type DeepPartial, defaultConfig, mergeConfig } from './config';
import { CommandService, tributeClaimOp } from './services/commands';
import { levelFromExp } from './game-rules';
import { type AuthState, type ConnectionContext, EventHub, type TransportKind } from './hub';
import { type Logger, noopLogger } from './logger';
import { type Migration, type MigrationReport, runMigrations } from './migrations';
import { KeyedMutex } from './mutex';
import type { PasswordHasher } from './password';
import { DefaultProfileProjector, type ProfileProjector } from './projector';
import { type StorageAdapter, deleteOp, isDocumentDamaged, putOp, skipDamaged } from './storage';
import { type AuthoritativeValidator, BasicProfileValidator, type ProfileValidator, DisabledValidator } from './validation';
import { AccountDeletionService, type AccountDeletionActor, type AccountDeletionReport, documentOwner } from './services/account-deletion';
import { AssaultService } from './services/assault';
import { ClanDirectory } from './services/clan-directory';
import { ClanService } from './services/clans';
import { ChatService } from './services/chat';
import { AuctionService } from './services/auction';
import { AUTH_FAILURE_MESSAGES, type AuthOutcome, AuthService, type SessionEndReason, TokenDigest, sessionKey } from './services/auth';
import { BattleService, type BattleServiceOptions } from './services/battles';
import { type AccountRecord, C, type ProfileRecord, type ServiceDeps } from './services/deps';
import { LeaderboardService } from './services/leaderboard';
import { GrantService, ProfileService } from './services/profile';
import { NewsService, ReplayService } from './services/replays';
import { MatchmakingService, RoomService } from './services/rooms';
import { SessionService } from './services/sessions';
import { AdsService } from './services/ads';
import { MonetizationAudit } from './services/monetization-audit';
import { PlayGamesService, type PlayGamesVerifier } from './services/play-games';
import { AccountAudit } from './services/account-audit';
import { ResourceLedger } from './services/resource-ledger';
import { AnticheatService } from './services/anticheat';
import { EmailService, type EmailSender } from './services/email';
import { IntegrityService, type IntegrityVerifier } from './services/integrity';
import { PushService, type PushSender } from './services/push';
import { type GooglePlayPurchases, StoreService } from './services/store';
import { AD_PLACEMENTS, type AdPlacementDef, type AdPlacementId, STORE_PRODUCTS, type StoreProductDef, catalogueProblems } from './monetization-catalog';
import { serverGameDefs } from './rules-runtime';

export interface BackendCoreOptions {
  storage: StorageAdapter;
  /** `server` enables rate limiting and turns off the offline conveniences (legacy import, developer profiles) by default. */
  mode?: 'in-process' | 'server';
  clock?: Clock;
  rng?: RandomSource;
  /** Random source of the rule ops (roulette, steals, crafting, battle seeds); default `rng`. */
  ruleRng?: RandomSource;
  /**
   * TEST / DEVELOPMENT SWITCH, never for production: every battle the backend starts (battle.start, solo and online
   * sessions) gets this seed (0..399) instead of a random one, so a scripted battle against a real server is
   * reproducible (the server's BATTLE_SEED). Unset / null: random seeds (the default). The rule RNG is still drawn as
   * usual, only the battle's seed is replaced.
   */
  fixedBattleSeed?: number | null;
  logger?: Logger;
  /** Enables username/password accounts; null disables them (guest accounts only). */
  passwordHasher?: PasswordHasher | null;
  /**
   * AUTH_SECRET: tokens are stored as HMAC-SHA256(secret, token). None: plain SHA-256 (the in-process backend; a server
   * warns at startup). Never logged.
   */
  authSecret?: string | null;
  /** Earlier AUTH_SECRETs, still accepted for lookups of records hashed with them (secret rotation). */
  authPreviousSecrets?: readonly string[];
  validator?: AuthoritativeValidator;
  /**
   * Re-simulates solo battles finished with player.command 'battle.finish' (services/battles.ts) and finished online
   * battles (services/sessions.ts). None: every such battle stays 'unverified' and the client's claim (the
   * participants' consensus) is applied. The creator owns it (closes it after `close()`).
   */
  simulator?: BattleSimulator | null;
  /** Limits of the battle service (simulation budget and wait, tick limits per mode, the annihilator source). */
  battles?: Omit<BattleServiceOptions, 'simulator'>;
  /** Overrides the trust-level default (null = accept every save). */
  profileValidator?: ProfileValidator | null;
  projector?: ProfileProjector;
  levelOf?: (exp: number) => number;
  config?: DeepPartial<BackendConfig>;
  /** Storage migrations to run before serving (default: the registry of ./migrations.ts). */
  migrations?: readonly Migration[];
  /**
   * Google Play Developer API client of the store (src/server/node/google-play.ts: purchases.products get / consume,
   * voided purchases). None: the provider 'google-play' is off (the catalogue is still served: without config.store
   * .devTest either, the shop shows its offers and nothing can be bought).
   */
  googlePlay?: GooglePlayPurchases | null;
  /** Verifies Play Games server auth codes (src/server/node/play-games.ts). None: the feature 'playGames' is off. */
  playGames?: PlayGamesVerifier | null;
  /**
   * Delivers the e-mails of the e-mail sign-in (src/server/node/email-senders.ts: EMAIL_PROVIDER). None, or no
   * config.email.linkBaseUrl, or no password hasher: the feature 'email' is off.
   */
  emailSender?: EmailSender | null;
  /** Decodes Play Integrity tokens (src/server/node/play-integrity.ts: PLAY_INTEGRITY). None: the feature 'integrity' is off. */
  integrity?: IntegrityVerifier | null;
  /** Sends push notifications (src/server/node/fcm.ts: FCM_SERVICE_ACCOUNT_FILE). None: the feature 'push' is off. */
  pushSender?: PushSender | null;
  /** The monetization catalogue (default: ./monetization-catalog.ts; tests pass their own). */
  catalogue?: { products?: readonly StoreProductDef[]; placements?: Readonly<Record<AdPlacementId, AdPlacementDef>> };
}

/** Buckets a TokenBucket keeps at most (see prune). */
const MAX_BUCKETS = 50_000;

class TokenBucket {
  /** In order of last use (a used bucket moves to the end). */
  private readonly buckets = new Map<string, { tokens: number; at: number }>();
  constructor(
    private readonly burst: number,
    private readonly perSecond: number,
  ) {}
  take(key: string, now: number): boolean {
    const b = this.buckets.get(key) ?? { tokens: this.burst, at: now };
    b.tokens = Math.min(this.burst, b.tokens + ((now - b.at) / 1000) * this.perSecond);
    b.at = now;
    this.buckets.delete(key);
    this.buckets.set(key, b);
    if (this.buckets.size > MAX_BUCKETS) this.prune(now);
    if (b.tokens < 1) return false;
    b.tokens -= 1;
    return true;
  }
  /**
   * Too many keys: the buckets that are full again go (they remember nothing), then the least recently used ones down
   * to 90% of the cap. Never all of them at once: many fresh keys (addresses of an IPv6 network, new connections) must
   * not give every throttled client a fresh budget.
   */
  private prune(now: number): void {
    for (const [k, b] of this.buckets) if (b.tokens + ((now - b.at) / 1000) * this.perSecond >= this.burst) this.buckets.delete(k);
    let excess = this.buckets.size - Math.floor(MAX_BUCKETS * 0.9);
    for (const k of this.buckets.keys()) {
      if (excess-- <= 0) break;
      this.buckets.delete(k);
    }
  }
  /** Gives back a token `take` granted (the request it paid for created nothing). */
  refund(key: string): void {
    const b = this.buckets.get(key);
    if (b) b.tokens = Math.min(this.burst, b.tokens + 1);
  }
}

/** Requests that hash a password (scrypt: tens of milliseconds of one of the few libuv threads each). */
const PASSWORD_REQUESTS: ReadonlySet<RequestType> = new Set<RequestType>([
  'auth.signUp',
  'auth.signIn',
  'auth.register',
  'auth.login',
  'auth.link',
  'account.delete',
  'auth.emailSignUp',
  'auth.emailSignIn',
  'auth.emailChange',
  'auth.passwordReset',
]);

/**
 * Password requests one client address (IPv6: its /64) may have running at once while rate limiting is on; more are
 * RATE_LIMITED 'rate_limited'. Without it a burst of sign-ins from one address queues hundreds of hashes on the shared
 * thread pool and every other player's sign-in waits behind them.
 */
export const MAX_PASSWORD_CHECKS_PER_ADDRESS = 4;

/** WebSocket close code of the connections of a player who was just banned (see setBan). */
export const BANNED_CLOSE_CODE = WS_CLOSE_CODES.banned;

/** WebSocket close code of the connections of an ended device session (logout elsewhere, auth.revoke, ...). */
export const SESSION_ENDED_CLOSE_CODE = WS_CLOSE_CODES.sessionEnded;

/** WebSocket close code of the connections of a session that a sign-in elsewhere replaced (one session per account). */
export const SESSION_REPLACED_CLOSE_CODE = WS_CLOSE_CODES.sessionReplaced;

/**
 * Requests that open or resume a device session: with one active session per account they end the account's other
 * sessions. The asking connection is taken out of its current session's connections first, so that the end of that
 * session (the same account signing in again on this very connection) does not close it.
 */
const SESSION_OPENING_REQUESTS: ReadonlySet<RequestType> = new Set<RequestType>([
  'auth.guest',
  'auth.signUp',
  'auth.signIn',
  'auth.register',
  'auth.login',
  'auth.playGames',
  'auth.emailSignUp',
  'auth.emailSignIn',
  'auth.resume',
  'auth.refresh',
  'auth.link',
]);

/** Longest wait of one events.poll (a proxy or a mobile network must not cut the long poll). */
export const MAX_POLL_WAIT_MS = 25_000;

/** What a handler may know about its request besides the payload. */
interface RequestInfo {
  /** The envelope's request id (auth.refresh: the retry key of a refresh whose reply was lost). */
  id: string;
}

type Handler<K extends RequestType> = (ctx: ConnectionContext, p: PayloadOf<K>, req: RequestInfo) => Promise<ResultOf<K>> | ResultOf<K>;

/** A cached mutation response (see the module comment). */
interface IdempotencyEntry {
  expires: number;
  payloadHash: string;
  response: Promise<ResponseEnvelope>;
  /** Sign-in requests: the session the request bound its connection to (a retry on another connection is bound too). */
  auth: AuthState | null;
  /** Characters of the reply as JSON, counted once it is known (config.idempotency.maxBytes). */
  bytes: number;
  /** The player the request was of (sign-ins: the player it signed in), for the account deletion; null when unknown. */
  owner: PlayerId | null;
}

/**
 * Error codes whose responses are not kept in the idempotency cache: the request was not executed (or failed
 * transiently), so a retry under the same id runs again.
 */
const NOT_CACHED_CODES: ReadonlySet<string> = new Set(['INTERNAL', 'UNAVAILABLE', 'RATE_LIMITED']);

/** Mutations whose replies are not stored for retries after a restart (see the module comment). */
export const NOT_PERSISTED: ReadonlySet<RequestType> = new Set<RequestType>(['player.command', 'profile.save', 'profile.importLegacy', 'session.input', 'session.hash', 'account.delete']);

/** A stored idempotency entry (collection C.idempotency). */
interface StoredIdempotencyDoc {
  v: 1;
  /** The document's own id (list() returns documents without their ids). */
  id: string;
  expires: number;
  /** sealJson of SealedIdempotencyEntry under the entry's seal key. */
  sealed: string;
  /**
   * Keyed hash of the player the reply belongs to (idempotencyOwnerTag): the account deletion finds the player's replies
   * by it without opening them. Absent in documents of earlier versions (they expire within the retry window).
   */
  owner?: string;
}

interface SealedIdempotencyEntry {
  type: RequestType;
  payloadHash: string;
  response: ResponseEnvelope;
  auth: AuthState | null;
}

/** Cheap runtime counters (GET /health). */
export interface BackendStats {
  /** Present players: at least one live push connection or an HTTP mailbox its client polls. */
  players: number;
  /** Authenticated push (WebSocket / in-process) connections. */
  pushConnections: number;
  /** HTTP long-poll mailboxes. */
  mailboxes: number;
  rooms: number;
  sessions: number;
}

/** Persists in-memory service state (e.g. active rooms and sessions) during flush()/close(). */
export type FlushHook = () => Promise<void> | void;

/** Live entries of a service: its `activeCount()` when it has one, else the size of its registry map. */
function liveCount(service: object, registry: string): number {
  const s = service as { activeCount?: () => number } & Record<string, unknown>;
  if (typeof s.activeCount === 'function') return s.activeCount();
  const m = s[registry];
  return m instanceof Map ? m.size : 0;
}

export class BackendCore {
  readonly config: BackendConfig;
  readonly clock: Clock;
  readonly logger: Logger;
  readonly storage: StorageAdapter;
  readonly hub: EventHub;
  readonly auth: AuthService;
  readonly grants: GrantService;
  readonly profiles: ProfileService;
  readonly leaderboard: LeaderboardService;
  readonly clans: ClanService;
  /** The players' chat: the global channel and the clans' channels (services/chat.ts). */
  readonly chat: ChatService;
  readonly assault: AssaultService;
  readonly auction: AuctionService;
  readonly replays: ReplayService;
  readonly news: NewsService;
  readonly sessions: SessionService;
  readonly rooms: RoomService;
  readonly matchmaking: MatchmakingService;
  readonly commands: CommandService;
  readonly battles: BattleService;
  readonly deletion: AccountDeletionService;
  /** The audit log of purchases and ad rewards (services/monetization-audit.ts). */
  readonly monetizationAudit: MonetizationAudit;
  readonly store: StoreService;
  readonly ads: AdsService;
  readonly playGames: PlayGamesService;
  /** The audit log of account security events (services/account-audit.ts). */
  readonly accountAudit: AccountAudit;
  /** Operators' changes of players' gold and diamonds (services/resource-ledger.ts; read by the anti-cheat). */
  readonly ledger: ResourceLedger;
  /** The anti-cheat: marks for the operators, never a block (services/anticheat.ts). */
  readonly anticheat: AnticheatService;
  readonly email: EmailService;
  readonly integrity: IntegrityService;
  readonly push: PushService;

  private readonly rng: RandomSource;
  private readonly limiter: TokenBucket | null;
  private readonly accountLimiter: TokenBucket | null;
  /** Password requests running per client address key (MAX_PASSWORD_CHECKS_PER_ADDRESS). */
  private readonly passwordChecks = new Map<string, number>();
  private readonly idempotency = new Map<string, IdempotencyEntry>();
  /** Sum of `bytes` of the entries of `idempotency`. */
  private idempotencyBytes = 0;
  /** Stored idempotency documents by id -> expiry, sealed size and owner tag, oldest first (pruned by the sweeper). */
  private readonly storedIdempotency = new Map<string, { expires: number; bytes: number; owner: string | null }>();
  /** Sum of `bytes` of `storedIdempotency`. */
  private storedIdempotencyBytes = 0;
  /** Documents stored by an earlier run of the server that no retry claimed yet (id -> document). */
  private readonly restoredIdempotency = new Map<string, StoredIdempotencyDoc>();
  /** Idempotency records being written (close() waits for them before the storage closes). */
  private readonly idempotencyWrites = new Set<Promise<void>>();
  /**
   * The storage writes of replies under way (a subset of idempotencyWrites: the reply is known, only its write is
   * pending). The account deletion waits for these, never for a reply still being computed (that one may wait for a
   * lock the deletion holds).
   */
  private readonly idempotencyStoring = new Set<Promise<void>>();
  /** Why a connection is not authenticated (a refused bearer token, an ended session); default 'session_required'. */
  private readonly authFailures = new WeakMap<ConnectionContext, AuthFailureReason>();
  /** Transport hooks that close a connection (WebSocket: close code 4001 when its session ends). */
  private readonly closers = new WeakMap<ConnectionContext, (code: number, reason: string) => void>();
  /**
   * Connections whose transport closed (closeConnection). A sign-in still running when its WebSocket closed must not
   * bind the dead connection afterwards: it would stay attached to the event hub and keep its player online for good.
   */
  private readonly closedConnections = new WeakSet<ConnectionContext>();
  /** Push connections by device session (services/auth.ts), to drop them when the session ends. */
  private readonly sessionConnections = new Map<string, Set<ConnectionContext>>();
  private sweeper: ReturnType<typeof setInterval> | null = null;
  private ticking: Promise<void> | null = null;
  private lastTokenSweep = 0;
  private closed = false;
  private readonly handlers: { [K in RequestType]: Handler<K> };

  private constructor(opts: BackendCoreOptions) {
    const mode = opts.mode ?? 'in-process';
    this.config = mergeConfig(defaultConfig(mode), opts.config);
    this.clock = opts.clock ?? systemClock;
    this.rng = opts.rng ?? cryptoRandomSource();
    this.logger = opts.logger ?? noopLogger;
    this.storage = opts.storage;
    this.hub = new EventHub(this.clock, undefined, this.config.events);
    const deps: ServiceDeps = {
      storage: this.storage,
      clock: this.clock,
      rng: this.rng,
      logger: this.logger,
      hub: this.hub,
      config: this.config,
      mutex: new KeyedMutex(),
      ruleRng: opts.ruleRng ?? this.rng,
      fixedBattleSeed: opts.fixedBattleSeed ?? null,
    };
    if (deps.fixedBattleSeed !== null) {
      if (!Number.isInteger(deps.fixedBattleSeed) || deps.fixedBattleSeed < 0 || deps.fixedBattleSeed > 399) {
        throw new RangeError(`fixedBattleSeed must be an integer in 0..399, got ${String(deps.fixedBattleSeed)}`);
      }
      this.logger.warn('every battle uses one fixed seed (BATTLE_SEED, a test and development switch): never set it in production', { battleSeed: deps.fixedBattleSeed });
    }
    const levelOf = opts.levelOf ?? levelFromExp;
    // L2 accepts saves only as imports into a fresh profile; those get the L1 range checks too.
    const profileValidator = opts.profileValidator !== undefined ? opts.profileValidator : this.config.trustLevel !== 'L0' ? new BasicProfileValidator() : null;
    this.auth = new AuthService(deps, opts.passwordHasher ?? null, new TokenDigest(opts.authSecret ?? null, opts.authPreviousSecrets ?? []));
    this.auth.onSessionsEnded((playerId, sessionIds, reason) => this.dropSessions(playerId, sessionIds, reason));
    if (mode === 'server' && !opts.authSecret) {
      this.logger.warn('AUTH_SECRET is not set: tokens are stored as plain SHA-256 hashes; set AUTH_SECRET to a random value of at least 32 characters');
    }
    this.grants = new GrantService(deps);
    // Clan membership: one index every reader uses (services/clan-directory.ts); ClanService is its only writer.
    const clanDirectory = new ClanDirectory(this.storage, this.logger);
    this.profiles = new ProfileService(deps, this.auth, this.grants, opts.projector ?? new DefaultProfileProjector(levelOf), profileValidator, levelOf, clanDirectory);
    this.leaderboard = new LeaderboardService(deps, this.auth, this.profiles, clanDirectory);
    this.replays = new ReplayService(deps);
    this.news = new NewsService(deps);
    this.sessions = new SessionService(deps, this.auth, this.replays, opts.validator ?? new DisabledValidator(), { simulator: opts.simulator ?? null });
    this.assault = new AssaultService(deps, this.auth, this.profiles, this.sessions, clanDirectory);
    this.clans = new ClanService(deps, this.auth, this.profiles, clanDirectory, this.assault);
    this.chat = new ChatService(deps, this.profiles, clanDirectory);
    this.auction = new AuctionService(deps, this.auth);
    this.rooms = new RoomService(deps, this.auth, this.sessions);
    this.matchmaking = new MatchmakingService(deps, this.rooms, this.leaderboard);
    this.commands = new CommandService(deps, this.profiles);
    this.battles = new BattleService(deps, this.profiles, this.assault, this.replays, { ...(opts.battles ?? {}), simulator: opts.simulator ?? null });
    this.battles.registerOps(this.commands);
    // The auction's Annihilators in every solo battle: (holder ? 1 : 0) + permanent copies (design D7).
    if (!opts.battles?.annihilators) this.battles.setAnnihilatorSource((playerId) => this.auction.annihilatorsOf(playerId));
    this.auction.registerOps(this.commands);
    this.commands.registerServiceOp('tribute.claim', tributeClaimOp(this.assault));
    // In-app purchases, opt-in rewarded ads and the Play Games link (the catalogue: ./monetization-catalog.ts).
    const products = opts.catalogue?.products ?? STORE_PRODUCTS;
    const placements = opts.catalogue?.placements ?? AD_PLACEMENTS;
    const problems = catalogueProblems(products, placements, Object.keys(serverGameDefs().STIM_DEF as object));
    if (problems.length > 0) throw new Error(`The monetization catalogue is invalid: ${problems.join('; ')}`);
    this.monetizationAudit = new MonetizationAudit(deps);
    this.ledger = new ResourceLedger(deps);
    this.store = new StoreService(deps, this.auth, this.profiles, this.monetizationAudit, {
      googlePlay: opts.googlePlay ?? null,
      accountKey: opts.authSecret ? `store:${opts.authSecret}` : 'fwp-in-process-store',
      products,
    });
    this.store.registerOps(this.commands);
    this.ads = new AdsService(deps, this.profiles, this.monetizationAudit, { userIdOf: (playerId) => this.store.accountTag(playerId), placements });
    this.playGames = new PlayGamesService(deps, this.monetizationAudit, opts.playGames ?? null);
    // E-mail sign-in, Play Integrity and push notifications: skeletons behind their configuration (each feature is off
    // without its sender / verifier).
    this.accountAudit = new AccountAudit(deps, (text) => this.auth.digest.hash(text));
    // A password reset is the account's recovery: a Play Games link (which somebody who knew the old password could
    // have made to a Play Games player of theirs) goes with the old password.
    this.email = new EmailService(deps, this.auth, this.accountAudit, opts.emailSender ?? null, async (playerId) => {
      const linked = (await this.playGames.status(playerId)).linked;
      if (linked) await this.playGames.unlink(playerId);
      return { playGamesUnlinked: linked };
    });
    this.integrity = new IntegrityService(deps, this.accountAudit, opts.integrity ?? null);
    // The anti-cheat hears every stored balance change with its source, every solo and online battle and the command
    // journal's repeats; it only marks players for the operators.
    this.anticheat = new AnticheatService(deps, { simulator: (opts.simulator ?? null) !== null });
    this.profiles.onEconomy((c) => this.anticheat.economy(c));
    this.battles.onFinished((f) => this.anticheat.battle(f));
    this.battles.onLost((f) => this.anticheat.battleLost(f));
    this.sessions.onEnded((info, facts) => this.anticheat.session(info, facts));
    this.sessions.onInputRefused((r) => this.anticheat.sessionRefusal(r));
    this.commands.onSignal((sig) => this.anticheat.command(sig));
    this.push = new PushService(deps, this.accountAudit, (text) => this.auth.digest.hash(text), opts.pushSender ?? null);
    // A device that is no longer signed in to the account (signed out, replaced, revoked, reset) gets none of its notifications.
    this.auth.onSessionsEnded((playerId, sessionIds, reason) => {
      void this.push.sessionsEnded(playerId, sessionIds, reason).catch((err) => deps.logger.error('push devices of ended sessions not removed', { playerId, err }));
    });
    // A purchase's audit entry names the player's latest Play Integrity verdict (a signal, never a refusal).
    if (this.integrity.enabled) this.monetizationAudit.setSignal((playerId) => this.integrity.signalOf(playerId));
    if (this.config.store.devTest && mode === 'server') this.logger.warn('STORE_DEV_TEST is on: purchases with a devtest. token are credited without any store (a development switch: never on a server that players use)');
    if (this.config.ads.devTest && mode === 'server') this.logger.warn('ADS_DEV_TEST is on: ad rewards of the dev-test provider need no ad at all (a development switch: never on a server that players use)');
    this.deletion = new AccountDeletionService(
      deps,
      {
        auth: this.auth,
        grants: this.grants,
        leaderboard: this.leaderboard,
        assault: this.assault,
        auction: this.auction,
        replays: this.replays,
        sessions: this.sessions,
        rooms: this.rooms,
        matchmaking: this.matchmaking,
        clans: this.clans,
        extra: [this.store, this.ads, this.playGames, this.email, this.integrity, this.push, this.accountAudit, this.ledger, this.anticheat, this.chat],
      },
      {
        storedReplies: async (playerId) => {
          await Promise.allSettled([...this.idempotencyStoring]);
          const tag = this.idempotencyOwnerTag(playerId);
          return [...this.storedIdempotency].filter(([, v]) => v.owner === tag).map(([id]) => id);
        },
        repliesDeleted: (ids) => {
          for (const id of ids) {
            this.untrackStored(id);
            this.restoredIdempotency.delete(id);
          }
        },
        forgetPlayer: (playerId) => this.forgetPlayer(playerId),
      },
    );
    const rl = this.config.rateLimit;
    this.limiter = rl.enabled ? new TokenBucket(rl.burst, rl.perSecond) : null;
    this.accountLimiter = rl.enabled ? new TokenBucket(rl.accountsPerIpBurst, rl.accountsPerIpPerHour / 3600) : null;
    this.handlers = this.createHandlers();
    // Active rooms and battle sessions are written at shutdown (and on every flush) besides their own writes.
    this.onFlush(async () => {
      await this.sessions.flush();
      await this.rooms.flush();
      await this.anticheat.flush();
      await this.chat.flushReads();
    });
  }

  static async create(opts: BackendCoreOptions): Promise<BackendCore> {
    await opts.storage.open();
    // Stored documents are brought to the current schema before anything reads them (./migrations.ts). A newer
    // schema stops a server; the in-process (offline) backend only warns.
    const mode = opts.mode ?? 'in-process';
    const storageSchema = await runMigrations(
      { storage: opts.storage, config: mergeConfig(defaultConfig(mode), opts.config), clock: opts.clock ?? systemClock, rng: opts.rng ?? cryptoRandomSource(), logger: opts.logger ?? noopLogger },
      { migrations: opts.migrations, onNewer: mode === 'server' ? 'fail' : 'warn' },
    );
    const core = new BackendCore(opts);
    core.storageSchema = storageSchema;
    await core.restoreIdempotency();
    // The anti-cheat's marks (the console reads them from memory).
    await core.anticheat.init();
    // The chat's channels and mutes.
    await core.chat.init();
    // Rooms and battle sessions that were running when the server stopped (the sessions first: a room's battle).
    await core.sessions.restore();
    await core.rooms.restore();
    // Account deletions the previous run stopped after their batch (a crash, a storage failure) are finished.
    await core.deletion.finishPending();
    return core;
  }

  /** What the storage migrations of create() found and did (schema version before / after, migrations applied). */
  storageSchema: MigrationReport = { from: 0, to: 0, applied: [] };

  /**
   * `collection/id` of the documents an operator repaired whose collection only a start reads into memory (live rooms
   * and sessions, stored replies): they take effect after the next restart (/health degraded 'restart_required').
   */
  readonly repairsAwaitingRestart: string[] = [];

  /**
   * Before an operator stores a damaged document's copy from a backup (admin server.repairDocument): the player it
   * belongs to (services/account-deletion.ts documentOwner) when that player's account is deleted, or its deletion is
   * not finished yet (null: no owner, or one that still exists). A deletion cannot remove what it could not read, so
   * such a copy would bring the deleted player's data back (a chat message under its nickname, a replay, a mark).
   */
  async deletedOwner(collection: string, id: string, value: unknown): Promise<PlayerId | null> {
    const owner = documentOwner(collection, id, value);
    if (owner === null) return null;
    const exists = async (c: string): Promise<boolean> => {
      try {
        return (await this.storage.get(c, owner)) !== null;
      } catch (err) {
        // A damaged account (or deletion record) is not a deleted one.
        if (isDocumentDamaged(err)) return c === C.accounts;
        throw err;
      }
    };
    if (collection === C.accounts && id === owner) return (await exists(C.accountDeletions)) ? owner : null;
    return !(await exists(C.accounts)) || (await exists(C.accountDeletions)) ? owner : null;
  }

  /**
   * After an operator stored a damaged document's copy in its place (admin server.repairDocument): the in-memory
   * indexes, built from their collections without it (every list left it out), take it in at once, so its player is
   * not stuck until a restart (the leaderboards, the clan directory, the stored battles and replays, grants, the chat, push tokens,
   * anti-cheat marks, open ad tickets, purchases to consume). A removed one (`value` null) changes nothing: no index had
   * it. Returns whether it waits for a restart (see repairsAwaitingRestart).
   */
  async documentRepaired(collection: string, id: string, value: unknown): Promise<{ restartRequired: boolean }> {
    if (value === null || typeof value !== 'object') return { restartRequired: false };
    const awaitRestart = () => {
      const key = `${collection}/${id}`;
      if (!this.repairsAwaitingRestart.includes(key)) this.repairsAwaitingRestart.push(key);
      return { restartRequired: true };
    };
    const v = value as never;
    try {
      switch (collection) {
        case C.profiles:
        case C.accounts:
          await this.leaderboard.repaired(id);
          break;
        case C.clans:
        case C.clanPlayers:
        case C.clanRequests:
          this.clans.directory.apply([putOp(collection, id, value)]);
          break;
        case C.battles:
          await this.sessions.archive.stored(v);
          break;
        case C.replays:
          await this.replays.repaired(v);
          break;
        case C.grants:
          await this.grants.repaired(v);
          break;
        case C.chatMessages:
        case C.chatMutes:
          await this.chat.repaired(collection, value);
          break;
        case C.pushTokens:
          await this.push.repaired(v);
          break;
        case C.anticheatMarks:
          await this.anticheat.repaired(v);
          break;
        case C.adTickets:
          this.ads.repaired(v);
          break;
        case C.purchases:
          this.store.repaired(v);
          break;
        case C.liveRooms:
        case C.liveSessions:
        case C.idempotency:
          this.logger.warn('repaired document takes effect after a restart', { collection, id });
          return awaitRestart();
      }
    } catch (err) {
      // (stored already: the indexes are rebuilt from the storage by the next start)
      this.logger.error('repaired document not taken into memory: it takes effect after a restart', { collection, id, err });
      return awaitRestart();
    }
    return { restartRequired: false };
  }

  /**
   * Enabled feature flags (system.hello): every flag except the ones the operator disabled (config.api
   * .disabledFeatures, FEATURES), 'credentials' without password accounts and 'legacyImport' where saves cannot be
   * imported (L2 without allowLegacyImport).
   */
  get features(): FeatureFlag[] {
    return ALL_FEATURES.filter((f) => this.featureEnabled(f));
  }

  /**
   * Whether a feature is on (see `features`). The router refuses the requests of every feature that is off with
   * FORBIDDEN { reason: 'feature_disabled', feature }, whether the operator switched it off or the configuration
   * implies it (no password hasher, L2 without allowLegacyImport).
   */
  featureEnabled(feature: FeatureFlag): boolean {
    if (this.config.api.disabledFeatures.includes(feature)) return false;
    if (feature === 'credentials') return this.auth.credentialsEnabled;
    if (feature === 'legacyImport') return this.config.trustLevel !== 'L2' || this.config.allowLegacyImport;
    // 'store': the catalogue is served everywhere (the shop's offers); purchases need a provider (store.catalog providers).
    if (feature === 'ads') return this.ads.enabled;
    if (feature === 'playGames') return this.playGames.enabled;
    if (feature === 'email') return this.email.enabled;
    if (feature === 'integrity') return this.integrity.enabled;
    if (feature === 'push') return this.push.enabled;
    return true;
  }

  private description: ApiDescription | null = null;

  /** The machine-readable API description (system.describe, GET /api/schema). */
  describe(): ApiDescription {
    this.description ??= describeApi({ serverVersion: this.config.api.serverVersion, publicBaseUrl: this.config.api.publicBaseUrl, enabledFeatures: this.features });
    return this.description;
  }

  /** Limits a client should respect (system.hello). */
  private limits(): HelloResult['limits'] {
    const rl = this.config.rateLimit;
    return {
      maxRequestBytes: LIMITS.maxRequestBytes,
      maxGameStateBytes: LIMITS.maxGameStateBytes,
      leaderboardPageSize: this.config.leaderboard.pageSize,
      leaderboardMaxPages: this.config.leaderboard.maxPages,
      maxInputBatch: LIMITS.maxInputBatch,
      pageSizes: { ...LIST_LIMITS, 'leaderboard.get': { default: this.config.leaderboard.pageSize, max: LIST_LIMITS['leaderboard.get'].max } },
      rateLimit: rl.enabled ? { burst: rl.burst, perSecond: rl.perSecond, accountsPerIpBurst: rl.accountsPerIpBurst, accountsPerIpPerHour: rl.accountsPerIpPerHour } : null,
      eventReplay: { maxEvents: this.config.events.maxEvents, retentionMs: this.config.events.retentionMs },
      maxPollWaitMs: MAX_POLL_WAIT_MS,
      clans: this.clans.limits(),
      chat: this.chat.limits(),
    };
  }

  // ------------------------------------------------------------------------------------------------------
  // Connections

  /**
   * A new transport connection. `close` lets the core close it (WebSocket: code 4001 when its device session is
   * ended by a logout, auth.revoke, the session limit or a detected refresh token reuse; 4004 when a sign-in of the
   * account elsewhere replaced it).
   */
  openConnection(opts: {
    transport: TransportKind;
    push?: ConnectionContext['push'];
    remoteAddress?: string | null;
    close?: (code: number, reason: string) => void;
  }): ConnectionContext {
    const ctx: ConnectionContext = {
      connectionId: randomId(this.rng, 'c_', 8),
      transport: opts.transport,
      remoteAddress: opts.remoteAddress ?? null,
      push: opts.push,
      auth: null,
      protocol: PROTOCOL_VERSION,
    };
    if (opts.close) this.closers.set(ctx, opts.close);
    return ctx;
  }

  /** The transport of a connection closed: it leaves the event hub and is never attached again (see bind). */
  closeConnection(ctx: ConnectionContext): void {
    this.closedConnections.add(ctx);
    this.detachConnection(ctx);
  }

  /** Takes a connection off the event hub and its session; the last one of a player reports the player offline. */
  private detachConnection(ctx: ConnectionContext): void {
    const pid = ctx.auth?.playerId ?? null;
    this.untrackSession(ctx);
    this.hub.detach(ctx);
    if (pid && !this.hub.isOnline(pid)) this.onOffline(pid);
  }

  /** HTTP: resolves the bearer token of a request into `ctx.auth` (a refused token keeps its reason for the reply). */
  async authenticateBearer(ctx: ConnectionContext, token: string | null): Promise<void> {
    ctx.auth = null;
    this.authFailures.delete(ctx);
    if (!token) return;
    const r = await this.auth.authenticate(token);
    if ('auth' in r) ctx.auth = r.auth;
    else this.authFailures.set(ctx, r.reason);
  }

  private bind(ctx: ConnectionContext, auth: AuthState): void {
    if (ctx.auth && ctx.auth.playerId !== auth.playerId) this.detachConnection(ctx);
    else this.untrackSession(ctx);
    const wasOnline = this.hub.isOnline(auth.playerId);
    ctx.auth = auth;
    this.authFailures.delete(ctx);
    if (ctx.push) {
      // A sign-in that finished after its WebSocket closed: the reply goes nowhere, the player is not made present.
      if (this.closedConnections.has(ctx)) return;
      this.hub.attach(ctx);
      this.trackSession(ctx);
    } else this.hub.mailbox(auth).lastPolledAt = this.clock.now();
    // A plain HTTP request keeps its mailbox (events collect from the first request) but makes the player present only
    // once the client polls it (Mailbox.polling, EventHub.isOnline).
    if (!wasOnline && this.hub.isOnline(auth.playerId)) this.onOnline(auth.playerId);
  }

  /** An HTTP client reads its mailbox (events.poll / events.resume): the mailbox makes its player present from now on. */
  private pollingMailbox(auth: AuthState): ReturnType<EventHub['mailbox']> {
    const wasOnline = this.hub.isOnline(auth.playerId);
    const mb = this.hub.mailbox(auth);
    mb.polling = true;
    mb.lastPolledAt = this.clock.now();
    if (!wasOnline && this.hub.isOnline(auth.playerId)) this.onOnline(auth.playerId);
    return mb;
  }

  private trackSession(ctx: ConnectionContext): void {
    const id = ctx.auth?.sessionId;
    if (!id || !ctx.push || this.closedConnections.has(ctx)) return;
    let set = this.sessionConnections.get(id);
    if (!set) this.sessionConnections.set(id, (set = new Set()));
    set.add(ctx);
  }

  private untrackSession(ctx: ConnectionContext): void {
    const id = ctx.auth?.sessionId;
    if (!id) return;
    const set = this.sessionConnections.get(id);
    if (!set) return;
    set.delete(ctx);
    if (set.size === 0) this.sessionConnections.delete(id);
  }

  /**
   * Unbinds a connection from its session (its requests answer UNAUTHORIZED `reason` from now on); `closeCode` also
   * closes it through its transport hook.
   */
  private unbind(ctx: ConnectionContext, reason: AuthFailureReason, closeCode?: number, closeText = 'session ended'): void {
    this.detachConnection(ctx);
    ctx.auth = null;
    if (reason === 'session_required') this.authFailures.delete(ctx);
    else this.authFailures.set(ctx, reason);
    if (closeCode !== undefined) {
      try {
        this.closers.get(ctx)?.(closeCode, closeText);
      } catch {
        /* a broken connection must not affect others */
      }
    }
  }

  /**
   * Ended device sessions: their HTTP mailboxes are dropped and their push connections closed (4001; 4004 'session
   * replaced' when a sign-in elsewhere replaced them: a later request on such a connection gets 'session_replaced').
   */
  private dropSessions(playerId: PlayerId, sessionIds: readonly string[], reason: SessionEndReason): void {
    const replaced = reason === 'replaced';
    const wasOnline = this.hub.isOnline(playerId);
    let closed = false;
    for (const id of sessionIds) this.hub.dropMailbox(sessionKey(id));
    for (const id of sessionIds) {
      for (const ctx of [...(this.sessionConnections.get(id) ?? [])]) {
        // The last one reports the player offline (an online battle's disconnect grace starts for it).
        if (replaced) this.unbind(ctx, 'session_replaced', SESSION_REPLACED_CLOSE_CODE, 'session replaced');
        else this.unbind(ctx, 'revoked', SESSION_ENDED_CLOSE_CODE);
        closed = true;
      }
      this.sessionConnections.delete(id);
    }
    if (wasOnline && !closed && !this.hub.isOnline(playerId)) this.onOffline(playerId);
  }

  /** UNAUTHORIZED with its stable reason (see AUTH_FAILURE_REASONS). */
  private unauthorized(reason: AuthFailureReason): ProtocolError {
    return { code: 'UNAUTHORIZED', message: AUTH_FAILURE_MESSAGES[reason], details: { reason } };
  }

  private onOnline(playerId: PlayerId): void {
    this.chat.playerOnline(playerId);
    this.rooms.setConnected(playerId, true);
    this.sessions.setConnected(playerId, true);
  }

  private onOffline(playerId: PlayerId): void {
    this.chat.playerOffline(playerId);
    this.matchmaking.cancelFor(playerId);
    this.rooms.setConnected(playerId, false);
    this.sessions.setConnected(playerId, false);
  }

  // ------------------------------------------------------------------------------------------------------
  // Request routing

  /**
   * Handles one request envelope of any transport. `opts.correlationId`: a transport-level correlation id (the HTTP
   * X-Correlation-Id header), used when the envelope has none. Every response carries the server time `ts` and echoes
   * the correlation id (a retry answered from the idempotency cache gets its own).
   */
  async handle(ctx: ConnectionContext, raw: unknown, opts: { correlationId?: string } = {}): Promise<ResponseEnvelope> {
    const own = typeof raw === 'object' && raw !== null ? correlationIdOf((raw as { correlationId?: unknown }).correlationId) : undefined;
    const correlationId = own ?? correlationIdOf(opts.correlationId);
    const response = await this.route(ctx, raw, correlationId);
    return withResponseMeta(response, { ts: this.clock.now(), correlationId });
  }

  private async route(ctx: ConnectionContext, raw: unknown, correlationId: string | undefined): Promise<ResponseEnvelope> {
    const parsed = parseRequestEnvelope(raw);
    if (!parsed.ok) return errorResponse(parsed.id, parsed.error, parsed.v);
    const env = parsed.envelope;
    const trace = { id: env.id, correlationId };
    const fail = (e: ProtocolError) => errorResponse(env.id, e, env.v);
    if (this.closed) return fail({ code: 'UNAVAILABLE', message: 'Backend is shutting down', details: { reason: 'shutting_down' } });
    if (!isRequestType(env.type)) {
      // Echoed as well-formed text: a lone surrogate in the answer would make strict JSON decoders refuse all of it.
      const type = wellFormedText(env.type);
      return fail({ code: 'BAD_REQUEST', message: `Unknown request type "${type}"`, details: { reason: PROTOCOL_ERROR_REASONS.unknownType, type } });
    }
    const feature = featureOf(env.type);
    if (feature !== null && !this.featureEnabled(feature)) {
      return fail({ code: 'FORBIDDEN', message: `The feature "${feature}" is disabled on this server`, details: { reason: 'feature_disabled', feature } });
    }
    const type: RequestType = env.type;
    const now = this.clock.now();
    if (this.limiter && !this.limiter.take(this.rateKey(ctx), now)) {
      return fail({ code: 'RATE_LIMITED', message: 'Too many requests', details: { reason: 'rate_limited', retryAfterMs: this.retryAfterMs() } });
    }
    const v = validatePayload(env.type, env.payload);
    if (!v.ok) return fail({ code: 'BAD_REQUEST', message: `Invalid payload: ${v.message}`, details: { reason: PROTOCOL_ERROR_REASONS.invalidPayload, issues: v.issues } });
    const meta = REQUEST_META[env.type];
    if (meta.auth) {
      // Every such request needs a live device session: an unexpired access token whose session was not ended.
      if (!ctx.auth) return fail(this.unauthorized(this.authFailures.get(ctx) ?? 'session_required'));
      const refused = await this.auth.validate(ctx.auth);
      if (refused === 'revoked' || refused === 'session_replaced') this.unbind(ctx, refused);
      if (refused) return fail(this.unauthorized(refused));
      try {
        this.auth.assertNotBanned(await this.auth.requireAccount(ctx.auth.playerId));
      } catch (err) {
        return fail(this.toProtocolError(err, env.type, trace));
      }
      if (!ctx.push) this.bind(ctx, ctx.auth);
    }
    const payloadHash = meta.mutating ? sha256Hex(JSON.stringify(v.value)) : '';
    const key = meta.mutating ? this.idempotencyKey(ctx, type, env.id, v.value, payloadHash, meta.auth) : null;
    if (key) {
      const hit = this.idempotency.get(key) ?? this.restoredEntry(key, type, now);
      if (hit && hit.expires > now) {
        if (hit.payloadHash !== payloadHash) return fail({ code: 'BAD_REQUEST', message: 'This request id was already used for another request', details: { reason: PROTOCOL_ERROR_REASONS.idReused } });
        if (meta.auth) return hit.response;
        // A retried sign-in (e.g. on a new WebSocket after the reply was lost) binds its connection like the first one.
        const replayed = await hit.response;
        const bound = hit.auth;
        if (bound && replayed.ok && ctx.auth?.sessionId !== bound.sessionId && (await this.auth.validate(bound)) === null) this.bind(ctx, bound);
        return replayed;
      }
    }
    const createsAccount = type === 'auth.register' || type === 'auth.signUp' || type === 'auth.emailSignUp' || (type === 'auth.guest' && !(v.value as PayloadOf<'auth.guest'>).deviceToken);
    // Keyed by the address's network for IPv6 (/64, see client-address.ts): rotating addresses of one /64 gets no fresh limit.
    const accountKey = createsAccount && this.accountLimiter && ctx.remoteAddress ? addressLimitKey(ctx.remoteAddress) : null;
    const hashKey = this.limiter && PASSWORD_REQUESTS.has(type) && ctx.remoteAddress ? addressLimitKey(ctx.remoteAddress) : null;
    if (hashKey !== null && (this.passwordChecks.get(hashKey) ?? 0) >= MAX_PASSWORD_CHECKS_PER_ADDRESS) {
      return fail({ code: 'RATE_LIMITED', message: 'Too many password checks at once from this address', details: { reason: 'rate_limited', retryAfterMs: 500 } });
    }
    if (accountKey !== null && !this.accountLimiter!.take(accountKey, now)) {
      return fail({ code: 'RATE_LIMITED', message: 'Too many new accounts from this address', details: { reason: 'too_many_accounts', retryAfterMs: Math.ceil(3_600_000 / Math.max(1e-9, this.config.rateLimit.accountsPerIpPerHour)) } });
    }
    if (hashKey !== null) this.passwordChecks.set(hashKey, (this.passwordChecks.get(hashKey) ?? 0) + 1);
    const entry: IdempotencyEntry | null = key
      ? { expires: now + this.config.idempotency.ttlMs, payloadHash, response: null as never, auth: null, bytes: 0, owner: meta.auth ? (ctx.auth?.playerId ?? null) : null }
      : null;
    // A sign-in on a connection bound to a session: the end of that session (replaced by this very sign-in) must not
    // close this connection; a failed sign-in leaves it in its session as before.
    const opening = SESSION_OPENING_REQUESTS.has(type) && ctx.auth ? ctx.auth : null;
    if (opening) this.untrackSession(ctx);
    const response = (async (): Promise<ResponseEnvelope> => {
      try {
        const handler = this.handlers[type] as Handler<RequestType>;
        const result = await handler(ctx, v.value as never, { id: env.id });
        if (entry && !meta.auth) {
          entry.auth = ctx.auth;
          entry.owner = ctx.auth?.playerId ?? null;
        }
        return okResponse(env.id, result, env.v);
      } catch (err) {
        if (opening && ctx.auth === opening) this.trackSession(ctx);
        // A refused sign-up (a taken or invalid nickname, a failure) created no account: it does not count against
        // the address's limit of new accounts, so a few typos never lock out everyone behind the same address.
        if (accountKey !== null) this.accountLimiter!.refund(accountKey);
        return fail(this.toProtocolError(err, env.type, trace));
      } finally {
        if (hashKey !== null) {
          const left = (this.passwordChecks.get(hashKey) ?? 1) - 1;
          if (left > 0) this.passwordChecks.set(hashKey, left);
          else this.passwordChecks.delete(hashKey);
        }
      }
    })();
    if (key && entry) {
      entry.response = response;
      this.idempotency.set(key, entry);
      this.trimIdempotency();
      void response.then((r) => {
        if (this.idempotency.get(key) !== entry) return;
        // A transient failure is not the outcome of the request: forget it, so that a retry under this id runs again.
        if (!r.ok && NOT_CACHED_CODES.has(r.error.code)) {
          this.dropIdempotency(key);
          return;
        }
        this.countIdempotency(entry, r);
      });
      // A successful mutation's reply is stored for retries after a restart (close() waits for the write).
      if (this.persistsIdempotency(type)) {
        const write = response.then((r) => (r.ok ? this.storeIdempotency(key, type, entry, r) : undefined));
        this.idempotencyWrites.add(write);
        void write.finally(() => this.idempotencyWrites.delete(write));
      }
    }
    return response;
  }

  // ------------------------------------------------------------------------------------------------------
  // Idempotency across restarts (see the module comment)

  /** Counts the size of an entry's reply and trims the cache to its bounds. */
  private countIdempotency(entry: IdempotencyEntry, reply: ResponseEnvelope): void {
    entry.bytes = JSON.stringify(reply).length;
    this.idempotencyBytes += entry.bytes;
    this.trimIdempotency();
  }

  private dropIdempotency(key: string): void {
    const entry = this.idempotency.get(key);
    if (!entry) return;
    this.idempotency.delete(key);
    this.idempotencyBytes -= entry.bytes;
  }

  /** Drops the oldest cached replies beyond config.idempotency.maxEntries / maxBytes. */
  private trimIdempotency(): void {
    const { maxEntries, maxBytes } = this.config.idempotency;
    for (const key of this.idempotency.keys()) {
      if (this.idempotency.size <= maxEntries && this.idempotencyBytes <= maxBytes) break;
      this.dropIdempotency(key);
    }
  }

  private persistsIdempotency(type: RequestType): boolean {
    return this.config.idempotency.persist && !NOT_PERSISTED.has(type);
  }

  /** The owner tag of a player's stored replies: a keyed hash of its id (the document names nobody in the clear). */
  private idempotencyOwnerTag(playerId: PlayerId): string {
    return this.auth.digest.hash(`fwp-idempotency-owner-v1|${playerId}`);
  }

  /** Document id and seal key of an idempotency key: two keyed hashes, neither reveals the other nor the key. */
  private idempotencyDocKeys(key: string): { id: string; seal: string } {
    return { id: this.auth.digest.hash(`fwp-idempotency-id-v1|${key}`), seal: this.auth.digest.hash(`fwp-idempotency-seal-v1|${key}`) };
  }

  private storeIdempotency(key: string, type: RequestType, entry: IdempotencyEntry, response: ResponseEnvelope): Promise<void> {
    const write = this.writeIdempotency(key, type, entry, response);
    this.idempotencyStoring.add(write);
    void write.finally(() => this.idempotencyStoring.delete(write));
    return write;
  }

  private async writeIdempotency(key: string, type: RequestType, entry: IdempotencyEntry, response: ResponseEnvelope): Promise<void> {
    const { id, seal } = this.idempotencyDocKeys(key);
    const body: SealedIdempotencyEntry = { type, payloadHash: entry.payloadHash, response, auth: entry.auth };
    const owner = entry.owner === null ? null : this.idempotencyOwnerTag(entry.owner);
    const doc: StoredIdempotencyDoc = { v: 1, id, expires: entry.expires, sealed: sealJson(seal, body, this.rng.bytes(16)), ...(owner ? { owner } : {}) };
    try {
      await this.storage.put(C.idempotency, id, doc);
      this.untrackStored(id);
      this.storedIdempotency.set(id, { expires: entry.expires, bytes: doc.sealed.length, owner });
      this.storedIdempotencyBytes += doc.sealed.length;
      const { maxEntries, maxBytes } = this.config.idempotency;
      const oldest: string[] = [];
      for (const old of this.storedIdempotency.keys()) {
        if (this.storedIdempotency.size <= maxEntries && this.storedIdempotencyBytes <= maxBytes) break;
        this.untrackStored(old);
        this.restoredIdempotency.delete(old);
        oldest.push(old);
      }
      await this.deleteIdempotencyDocs(oldest);
    } catch (err) {
      // The mutation happened: its reply goes out; only a retry after a restart would run it again.
      this.logger.error('idempotency record not stored', { type, err });
    }
  }

  /**
   * The entry an earlier run of the server stored for this key (a retry after a restart), moved into the in-memory
   * cache; null when there is none or it cannot be opened (another AUTH_SECRET, a damaged document).
   */
  private restoredEntry(key: string, type: RequestType, now: number): IdempotencyEntry | null {
    if (this.restoredIdempotency.size === 0 || !this.persistsIdempotency(type)) return null;
    const { id, seal } = this.idempotencyDocKeys(key);
    const doc = this.restoredIdempotency.get(id);
    if (!doc) return null;
    this.restoredIdempotency.delete(id);
    const body = doc.expires > now ? openSealedJson<SealedIdempotencyEntry>(seal, doc.sealed) : null;
    if (!body || body.type !== type || !body.response || body.response.ok !== true) return null;
    const entry: IdempotencyEntry = { expires: doc.expires, payloadHash: body.payloadHash, response: Promise.resolve(body.response), auth: body.auth ?? null, bytes: 0, owner: body.auth?.playerId ?? null };
    this.idempotency.set(key, entry);
    this.countIdempotency(entry, body.response);
    return entry;
  }

  /** Start: loads the stored idempotency documents; expired ones (and all of them when persistence is off) are deleted. */
  private async restoreIdempotency(): Promise<void> {
    const docs = await this.storage.list<StoredIdempotencyDoc>(C.idempotency);
    const now = this.clock.now();
    const expired: string[] = [];
    for (const doc of docs) {
      if (!doc || typeof doc.id !== 'string') continue;
      if (typeof doc.expires !== 'number' || doc.expires <= now || !this.config.idempotency.persist) {
        expired.push(doc.id);
        continue;
      }
      this.restoredIdempotency.set(doc.id, doc);
      const bytes = typeof doc.sealed === 'string' ? doc.sealed.length : 0;
      this.storedIdempotency.set(doc.id, { expires: doc.expires, bytes, owner: typeof doc.owner === 'string' ? doc.owner : null });
      this.storedIdempotencyBytes += bytes;
    }
    await this.deleteIdempotencyDocs(expired);
    if (this.restoredIdempotency.size > 0) this.logger.info('idempotency records restored', { records: this.restoredIdempotency.size });
  }

  /**
   * Key of a mutation in the idempotency cache. Request ids are unique per device session (envelope.ts), so the cache
   * is scoped to the session (the player for sessions of an earlier backend): two devices of one account may use the
   * same ids. Sign-in requests have no session yet: their key carries the payload hash, and so a credential only the
   * retrying client knows (password, device token); a guest sign-in without a device token carries none, so its key
   * adds the client address (the connection in-process). Their replies hold tokens: a key someone else can guess must
   * never answer them.
   */
  private idempotencyKey(ctx: ConnectionContext, type: RequestType, id: string, payload: unknown, payloadHash: string, authenticated: boolean): string | null {
    if (authenticated) return ctx.auth ? `s:${ctx.auth.sessionId ?? ctx.auth.playerId}|${type}|${id}` : null;
    const secretInPayload =
      type === 'auth.register' ||
      type === 'auth.login' ||
      type === 'auth.signUp' ||
      type === 'auth.signIn' ||
      type === 'auth.playGames' ||
      type === 'auth.emailSignUp' ||
      type === 'auth.emailSignIn' ||
      type === 'auth.emailVerify' ||
      type === 'auth.passwordReset' ||
      (type === 'auth.guest' && !!(payload as PayloadOf<'auth.guest'>).deviceToken);
    const client = secretInPayload ? '' : `|${ctx.remoteAddress ?? `c:${ctx.connectionId}`}`;
    return `a:${type}|${id}|${payloadHash}${client}`;
  }

  /** When a rate-limited client may try again (details.retryAfterMs): the time one request token takes to refill. */
  private retryAfterMs(): number {
    return Math.max(1, Math.ceil(1000 / Math.max(1e-9, this.config.rateLimit.perSecond)));
  }

  /**
   * Rate-limit key: the session token; else the client address (IPv6: its /64), one budget for every unauthenticated
   * HTTP request and WebSocket of that address (a new WebSocket must not bring a fresh budget); else (in-process, no
   * address) the connection.
   */
  private rateKey(ctx: ConnectionContext): string {
    if (ctx.auth) return `t:${ctx.auth.tokenHash}`;
    if (ctx.remoteAddress) return `a:${addressLimitKey(ctx.remoteAddress)}`;
    return `c:${ctx.connectionId}`;
  }

  /** A thrown error as the protocol error of the response; unexpected ones are logged with the request's ids. */
  private toProtocolError(err: unknown, type: string, trace: { id?: string; correlationId?: string } = {}): ProtocolError {
    if (err instanceof BackendError) return err.toProtocol();
    this.logger.error('request failed', { type, reqId: trace.id, cid: trace.correlationId, err });
    return { code: 'INTERNAL', message: 'Internal server error', details: { reason: 'internal_error' } };
  }

  private me(ctx: ConnectionContext): PlayerId {
    if (!ctx.auth) throw new BackendError('UNAUTHORIZED', 'Authentication required', { reason: 'session_required' });
    return ctx.auth.playerId;
  }

  /** auth.signUp (and the account of auth.emailSignUp): the account, its session and its initial game state are one batch. */
  private async signUp(ctx: ConnectionContext, p: PayloadOf<'auth.signUp'>): Promise<ResultOf<'auth.signUp'>> {
    let created: { rec: ProfileRecord; account: AccountRecord } | null = null;
    const outcome = await this.auth.signUp(p, (account) => {
      const rec = this.profiles.initialRecord(account);
      created = { rec, account };
      return [putOp(C.profiles, account.playerId, rec)];
    });
    const done = created as { rec: ProfileRecord; account: AccountRecord } | null;
    if (done) this.profiles.announce(done.rec, done.account);
    const result = this.authed(ctx, outcome);
    return { ...result, player: await this.profiles.playerCard(result.playerId) };
  }

  private createHandlers(): { [K in RequestType]: Handler<K> } {
    const me = (ctx: ConnectionContext) => this.me(ctx);
    return {
      'system.hello': (ctx, p) => {
        const client = { clientVersion: p.clientVersion, platform: p.platform ?? null, capabilities: [...new Set(p.capabilities ?? [])] };
        const protocol = negotiateProtocolVersion(p.protocols);
        const minClientVersion = this.config.api.minClientVersion;
        if (protocol === null) {
          this.logger.info('client refused', { conn: ctx.connectionId, reason: 'unsupported_protocol', protocols: p.protocols, ...client });
          throw new BackendError('VERSION_MISMATCH', 'No common protocol version', { reason: 'unsupported_protocol', supported: [...SUPPORTED_PROTOCOL_VERSIONS] });
        }
        if (isClientTooOld(p.clientVersion, minClientVersion)) {
          this.logger.info('client refused', { conn: ctx.connectionId, reason: 'client_too_old', minClientVersion, ...client });
          throw new BackendError('VERSION_MISMATCH', `Client version "${p.clientVersion}" is too old: update to ${minClientVersion} or later`, {
            reason: 'client_too_old',
            minClientVersion,
            clientVersion: p.clientVersion,
          });
        }
        ctx.protocol = protocol;
        ctx.client = client;
        this.logger.info('client hello', { conn: ctx.connectionId, transport: ctx.transport, protocol, ...client });
        return {
          serverVersion: this.config.api.serverVersion,
          protocol,
          minClientVersion,
          serverTime: this.clock.now(),
          features: this.features,
          limits: this.limits(),
          publicBaseUrl: this.config.api.publicBaseUrl,
        };
      },
      'system.time': () => ({ serverTime: this.clock.now() }),
      'system.describe': () => this.describe(),
      'events.poll': async (ctx, p) => {
        // Push connections get their events pushed; a poll there only reports the position (no waiting).
        if (ctx.push || !ctx.auth) return this.hub.resume(ctx, p.after);
        const mb = this.pollingMailbox(ctx.auth);
        const batch = await mb.poll(p.after, Math.min(p.waitMs ?? 0, MAX_POLL_WAIT_MS));
        mb.lastPolledAt = this.clock.now();
        return batch;
      },
      'events.resume': (ctx, p) => {
        if (!ctx.push && ctx.auth) this.pollingMailbox(ctx.auth);
        return this.hub.resume(ctx, p.after);
      },

      'auth.guest': async (ctx, p) => this.authed(ctx, await this.auth.guest(p)),
      'auth.signUp': (ctx, p) => this.signUp(ctx, p),
      'auth.signIn': async (ctx, p) => {
        const result = this.authed(ctx, await this.auth.signIn(p, { address: ctx.remoteAddress }));
        return { ...result, player: await this.profiles.playerCard(result.playerId) };
      },
      'auth.session': async (ctx) => ({ session: await this.auth.currentSession(ctx.auth!), player: await this.profiles.playerCard(me(ctx)) }),
      'auth.register': async (ctx, p) => this.authed(ctx, await this.auth.register(p)),
      'auth.login': async (ctx, p) => this.authed(ctx, await this.auth.login(p, { address: ctx.remoteAddress })),
      'auth.playGames': async (ctx, p) => {
        const gamesPlayerId = await this.playGames.verify(p.serverAuthCode);
        const playerId = await this.playGames.linkedAccount(gamesPlayerId);
        if (!playerId) throw new BackendError('NOT_FOUND', 'No account is linked to this Play Games player', { reason: 'play_games_not_linked' });
        const result = this.authed(ctx, await this.auth.signInVerified(playerId, 'play_games', p.device));
        return { ...result, player: await this.profiles.playerCard(result.playerId) };
      },
      'auth.linkPlayGames': (ctx, p) => this.playGames.link(me(ctx), p.serverAuthCode),
      'auth.emailSignUp': async (ctx, p) => {
        // The address is checked first: a refused one creates no account.
        const addr = this.email.checkAddress(p.email);
        const result = await this.signUp(ctx, { nickname: p.nickname, password: p.password, device: p.device });
        try {
          await this.email.afterSignUp(result.playerId, addr, p.lang);
        } catch (err) {
          // The account exists and is signed in: the address can be added again later (auth.emailChange).
          this.logger.warn('e-mail of a new account not requested', { playerId: result.playerId, err: err instanceof Error ? err.message : String(err) });
        }
        return result;
      },
      'auth.emailSignIn': async (ctx, p) => {
        const result = this.authed(ctx, await this.email.signIn(p, { address: ctx.remoteAddress }));
        return { ...result, player: await this.profiles.playerCard(result.playerId) };
      },
      'auth.emailStatus': (ctx) => this.email.statusOf(me(ctx)),
      'auth.emailChange': (ctx, p) => this.email.change(me(ctx), p, { address: ctx.remoteAddress }),
      'auth.emailResend': (ctx, p) => this.email.resend(me(ctx), p.lang),
      'auth.emailVerify': (_ctx, p) => this.email.verify(p.token),
      'auth.passwordResetRequest': (ctx, p) => this.email.requestReset(p, { address: ctx.remoteAddress }),
      'auth.passwordReset': (_ctx, p) => this.email.reset(p),
      'integrity.challenge': (ctx) => this.integrity.challenge(me(ctx)),
      'integrity.verify': (ctx, p) => this.integrity.verify(me(ctx), p.token, p.challenge),
      'push.register': (ctx, p) => this.push.register(me(ctx), ctx.auth?.sessionId ?? null, p),
      'push.unregister': (ctx, p) => this.push.unregister(me(ctx), p.token),
      'push.test': (ctx) => this.push.test(me(ctx)),
      'auth.unlinkPlayGames': (ctx) => this.playGames.unlink(me(ctx)),
      'auth.playGamesStatus': (ctx) => this.playGames.status(me(ctx)),
      'auth.resume': async (ctx, p) => this.authed(ctx, await this.auth.resume(p.token, p.device)),
      'auth.refresh': async (ctx, p, req) => this.authed(ctx, await this.auth.refresh(p.refreshToken, p.device, req.id)),
      'auth.link': async (ctx, p) => this.authed(ctx, await this.auth.link(me(ctx), ctx.auth?.sessionId ?? null, p)),
      'auth.sessions': async (ctx) => ({ sessions: await this.auth.listSessions(me(ctx), ctx.auth?.sessionId ?? null) }),
      'auth.revoke': async (ctx, p) => {
        const auth = ctx.auth!;
        // The asking connection is not closed: it answers, then may sign in again (its other connections are closed).
        if (auth.sessionId === p.sessionId) this.unbind(ctx, 'session_required');
        const ended = await this.auth.endSessions(auth.playerId, [p.sessionId], auth.sessionId === p.sessionId ? 'logout' : 'revoked');
        return { revoked: ended.length };
      },
      'auth.logout': async (ctx, p) => {
        const auth = ctx.auth!;
        this.unbind(ctx, 'session_required');
        const ended = await this.auth.endSessions(auth.playerId, p.all ? 'all' : auth.sessionId ? [auth.sessionId] : [], 'logout');
        return { revoked: ended.length };
      },

      'account.delete': async (ctx, p) => {
        const auth = ctx.auth!;
        await this.auth.confirmDeletion(await this.auth.requireAccount(auth.playerId), p.password, { address: ctx.remoteAddress });
        // The asking connection answers, then stays open without a session (as auth.logout); the others are closed.
        this.unbind(ctx, 'session_required');
        const report = await this.deleteAccount(auth.playerId, 'self');
        return { playerId: auth.playerId, deletedAt: report?.deletedAt ?? this.clock.now() };
      },

      'profile.load': async (ctx, p) => {
        // An abandoned solo battle (battle.start more than 6 h ago) ends as a loss before the state is handed out.
        await this.battles.expireAbandoned(me(ctx)).catch((err) => this.logger.error('abandoned battle check failed', { playerId: me(ctx), err }));
        return this.profiles.load(me(ctx), p);
      },
      'profile.save': (ctx, p) => this.profiles.save(me(ctx), p, ctx),
      'profile.me': (ctx) => this.profiles.playerCard(me(ctx)),
      'profile.setDisplayName': async (ctx, p) => {
        const r = await this.profiles.setDisplayName(me(ctx), p.displayName);
        await this.leaderboard.refreshAccount(me(ctx));
        await this.chat.renamed(me(ctx));
        return r;
      },
      'profile.getPublic': async (_ctx, p) => {
        const profile = await this.profiles.getPublic(p.playerId);
        if (!profile) throw new BackendError('NOT_FOUND', 'Player not found', { reason: 'player_not_found', playerId: p.playerId });
        return { profile };
      },
      'profile.getPublicBatch': async (_ctx, p) => {
        const profiles = [];
        for (const id of new Set(p.playerIds)) {
          // A damaged account or profile is left out like a missing one (profile.getPublic of it answers UNAVAILABLE).
          const pr = await skipDamaged(() => this.profiles.getPublic(id), this.logger, { playerId: id });
          if (pr) profiles.push(pr);
        }
        return { profiles };
      },
      'profile.importLegacy': (ctx, p) => this.profiles.importLegacy(me(ctx), p.legacy, p.baseRevision, ctx),
      'profile.listGrants': async (ctx, p) => {
        const page = await this.grants.pendingPage(me(ctx), { limit: p.limit, cursor: p.cursor });
        return { grants: page.items, items: page.items, next: page.next };
      },

      'player.command': (ctx, p) => this.commands.execute(me(ctx), p, ctx),

      'leaderboard.get': (ctx, p) => this.leaderboard.get(p.board, p.page, p.pageSize, me(ctx), { limit: p.limit, cursor: p.cursor }),

      'assault.getZones': async () => ({ serverTime: this.clock.now(), zones: await this.assault.getZones() }),
      'assault.getTargets': async (ctx, p) => ({ serverTime: this.clock.now(), targets: await this.assault.getTargets(me(ctx), p.zone) }),
      'assault.report': (ctx, p) => this.assault.report(me(ctx), p, ctx),
      'assault.claimTribute': (ctx) => this.assault.claimTribute(me(ctx)),
      'clan.list': async (_ctx, p) => {
        const page = await this.clans.list({ sort: p.sort, query: p.query, limit: p.limit, cursor: p.cursor });
        return { clans: page.items, items: page.items, next: page.next };
      },
      'clan.get': async (_ctx, p) => ({ clan: await this.clans.get(p.clanId) }),
      'clan.mine': (ctx) => this.clans.mine(me(ctx)),
      'clan.members': async (_ctx, p) => {
        const page = await this.clans.membersPage(p.clanId, { limit: p.limit, cursor: p.cursor });
        return { clanId: p.clanId, members: page.items, items: page.items, next: page.next };
      },
      'clan.create': (ctx, p) => this.clans.create(me(ctx), p),
      'clan.update': (ctx, p) => this.clans.update(me(ctx), p.clanId, { name: p.name, emblem: p.emblem, joinPolicy: p.joinPolicy, description: p.description }),
      'clan.join': (ctx, p) => this.clans.join(me(ctx), p.clanId),
      'clan.request': (ctx, p) => this.clans.request(me(ctx), p.clanId),
      'clan.cancelRequest': (ctx, p) => this.clans.cancelRequest(me(ctx), p.clanId),
      'clan.requests': (ctx, p) => this.clans.requestsPage(me(ctx), p.clanId, { limit: p.limit, cursor: p.cursor }),
      'clan.decide': (ctx, p) => this.clans.decide(me(ctx), p.clanId, p.playerId, p.accept),
      'clan.kick': (ctx, p) => this.clans.kick(me(ctx), p.clanId, p.playerId),
      'clan.setRole': (ctx, p) => this.clans.setRole(me(ctx), p.clanId, p.playerId, p.role),
      'clan.transfer': (ctx, p) => this.clans.transfer(me(ctx), p.clanId, p.playerId),
      'clan.leave': (ctx) => this.clans.leave(me(ctx)),
      'clan.disband': (ctx, p) => this.clans.disband(me(ctx), p.clanId, p.confirmName),
      'clan.history': (_ctx, p) => this.clans.historyPage(p.clanId, { limit: p.limit, cursor: p.cursor }),

      'chat.channels': (ctx, p) => this.chat.channelsFor(me(ctx), p.exclude),
      'chat.history': (ctx, p) => this.chat.history(me(ctx), p.channel, { limit: p.limit, cursor: p.cursor }),
      'chat.send': (ctx, p) => this.chat.send(me(ctx), p, ctx),
      'chat.subscribe': (ctx, p) => this.chat.subscribe(me(ctx), p.channels),
      'chat.unsubscribe': (ctx, p) => this.chat.unsubscribe(me(ctx), p.channels),
      'chat.markRead': (ctx, p) => this.chat.markRead(me(ctx), p.channel, p.seq),

      'auction.get': (ctx) => this.auction.info(me(ctx)),
      'store.catalog': (ctx) => this.store.catalog(me(ctx)),
      'ads.status': (ctx) => this.ads.status(me(ctx)),
      'ads.start': async (ctx, p) => ({ ticket: await this.ads.start(me(ctx), p.placement, p.provider, p.context ?? {}) }),
      'ads.ticket': async (ctx, p) => ({ ticket: await this.ads.ticket(me(ctx), p.ticketId) }),
      'ads.testReward': async (ctx, p) => ({ ticket: await this.ads.testReward(me(ctx), p.ticketId) }),

      'news.list': (_ctx, p) => this.news.page({ limit: p.limit, cursor: p.cursor }),

      'replay.save': async (ctx, p) => ({ replayId: (await this.replays.save(me(ctx), p)).replayId }),
      'replay.list': (ctx, p) => this.replays.page(p.playerId ?? me(ctx), { limit: p.limit, cursor: p.cursor }),
      'replay.get': async (ctx, p) => ({ replay: await this.replays.get(p.replayId, me(ctx)) }),

      'matchmaking.join': async (ctx, p) => ({ ticket: await this.matchmaking.join(me(ctx), p, ctx) }),
      'matchmaking.leave': (ctx, p) => {
        this.matchmaking.leave(me(ctx), p.ticketId);
        return {};
      },
      'room.list': (_ctx, p) => {
        const page = this.rooms.page({ limit: p.limit, cursor: p.cursor });
        return { rooms: page.items, items: page.items, next: page.next };
      },
      'room.get': (ctx, p) => ({ room: this.rooms.get(me(ctx), p.roomId) }),
      'room.create': async (ctx, p) => ({ room: await this.rooms.create(me(ctx), p) }),
      'room.join': async (ctx, p) => ({ room: await this.rooms.join(me(ctx), p) }),
      'room.leave': (ctx, p) => {
        this.rooms.leave(me(ctx), p.roomId);
        return {};
      },
      'room.update': async (ctx, p) => ({ room: this.rooms.update(me(ctx), p) }),
      'room.setReady': (ctx, p) => ({ room: this.rooms.setReady(me(ctx), p) }),
      'room.setTeam': (ctx, p) => ({ room: this.rooms.setTeam(me(ctx), p) }),
      'room.chat': async (ctx, p) => {
        await this.rooms.chat(me(ctx), p, (text) => this.chat.vetRoomMessage(me(ctx), text, ctx));
        return {};
      },
      'room.start': async (ctx, p) => ({ session: await this.rooms.start(me(ctx), p.roomId) }),

      'session.start': async (ctx, p) => ({ session: await this.sessions.start(me(ctx), p.init) }),
      'session.get': (ctx, p) => this.sessions.getAny(me(ctx), p.sessionId),
      'session.input': (ctx, p) => this.sessions.input(me(ctx), p, ctx),
      'session.endTurn': (ctx, p) => this.sessions.endTurn(me(ctx), p, ctx),
      'session.hash': (ctx, p) => this.sessions.hash(me(ctx), p),
      'session.finish': (ctx, p) => this.sessions.finish(me(ctx), p),
      'session.forfeit': async (ctx, p) => {
        await this.sessions.forfeit(me(ctx), p.sessionId);
        return {};
      },
      'session.dispute': (ctx, p) => this.sessions.dispute(me(ctx), p),
    };
  }

  /** Binds the connection to the session an auth request opened or resumed and returns the protocol result. */
  private authed<R extends AuthResult>(ctx: ConnectionContext, outcome: AuthOutcome<R>): R {
    this.bind(ctx, outcome.auth);
    return outcome.result;
  }

  // ------------------------------------------------------------------------------------------------------
  // Operator API (not reachable through the protocol)

  /**
   * Bans (or with `null` unbans) a player; takes effect on the next request and in the leaderboards. A ban also cuts
   * the player off at once: every push connection of the player leaves the event hub (no more events) and is closed
   * (WebSocket code 4003), and the HTTP mailboxes are dropped. The device sessions stay: the next request, or the
   * auth.resume of a client that reconnects, gets BANNED (the game's ban screen), and an unban lets it back without
   * a new sign-in.
   */
  async setBan(playerId: PlayerId, ban: { code: number; reason: string; until: number | null } | null): Promise<void> {
    await this.auth.setBan(playerId, ban);
    await this.leaderboard.refreshAccount(playerId);
    if (ban && (ban.until === null || ban.until > this.clock.now())) await this.dropPlayer(playerId, BANNED_CLOSE_CODE);
  }

  /**
   * Deletes the player's account and everything the backend keeps of it (services/account-deletion.ts): the routine
   * behind account.delete (`by` 'self') and the operator console's players.delete ('operator'). Returns what it did,
   * or null when no such account exists (a repeated deletion does nothing).
   */
  deleteAccount(playerId: PlayerId, by: AccountDeletionActor): Promise<AccountDeletionReport | null> {
    return this.deletion.delete(playerId, by);
  }

  /**
   * The end of an account deletion: the player's remaining push connections are unbound and closed (4001), its
   * mailboxes and event log dropped, and the replies of its requests leave the idempotency cache.
   */
  private forgetPlayer(playerId: PlayerId): void {
    for (const ctx of this.hub.connectionsOf(playerId)) this.unbind(ctx, 'revoked', SESSION_ENDED_CLOSE_CODE);
    for (const [id, set] of [...this.sessionConnections]) {
      for (const ctx of [...set]) if (ctx.auth?.playerId === playerId) this.unbind(ctx, 'revoked', SESSION_ENDED_CLOSE_CODE);
      if (set.size === 0) this.sessionConnections.delete(id);
    }
    this.hub.forgetPlayer(playerId);
    this.anticheat.forget(playerId);
    this.chat.forget(playerId);
    for (const [key, entry] of [...this.idempotency]) if (entry.owner === playerId || entry.auth?.playerId === playerId) this.dropIdempotency(key);
  }

  /**
   * Takes every push connection of the player off the event hub and closes it through its transport (`closeCode`),
   * and drops its sessions' HTTP mailboxes. The connections stay bound, so a request that still arrives on one is
   * answered by the router's own checks (BANNED).
   */
  private async dropPlayer(playerId: PlayerId, closeCode: number): Promise<void> {
    const sessions = await this.auth.listSessions(playerId, null);
    const wasOnline = this.hub.isOnline(playerId);
    let closed = false;
    for (const s of sessions) this.hub.dropMailbox(sessionKey(s.sessionId));
    for (const conns of [...this.sessionConnections.values()]) {
      for (const ctx of [...conns]) {
        if (ctx.auth?.playerId !== playerId) continue;
        this.detachConnection(ctx); // the last one reports the player offline
        closed = true;
        try {
          this.closers.get(ctx)?.(closeCode, 'banned');
        } catch {
          /* a broken connection must not affect others */
        }
      }
    }
    if (wasOnline && !closed && !this.hub.isOnline(playerId)) this.onOffline(playerId);
  }

  // ------------------------------------------------------------------------------------------------------
  // Background work

  /**
   * Runs the periodic sweeps once (auction rounds, turn deadlines, mailbox expiry, cleanup). Each step runs on its own:
   * a step that fails (a damaged document, a storage failure) is logged and the next steps still run, so one broken
   * part never stops the turn deadlines, the lobby expiry or the offline reports of everybody.
   */
  tick(now = this.clock.now()): Promise<void> {
    if (this.ticking) return this.ticking;
    this.ticking = (async () => {
      try {
        await this.sweepStep('auction', now, () => this.auction.resolveDue(now));
        await this.sweepStep('sessions', now, () => this.sessions.sweep(now));
        await this.sweepStep('rooms', now, () => this.rooms.sweep(now));
        await this.sweepStep('matchmaking', now, () => this.matchmaking.sweep(now));
        await this.sweepStep('presence', now, () => {
          for (const pid of this.hub.sweep(now)) this.onOffline(pid);
        });
        // Store and ad jobs may wait on the network: they run on their own (one at a time each), never holding the sweep.
        void this.store.tick(now);
        void this.ads.tick(now);
        // The anti-cheat's reconciliation reads profiles in batches: it runs on its own too (one run at a time).
        void this.anticheat.tick(now);
        await this.sweepStep('chat', now, () => this.chat.sweep(now));
        await this.sweepStep('idempotency', now, async () => {
          for (const [k, v] of this.idempotency) if (v.expires <= now) this.dropIdempotency(k);
          await this.pruneStoredIdempotency(now);
        });
        if (now - this.lastTokenSweep > 10 * 60_000) {
          this.lastTokenSweep = now;
          await this.sweepStep('auth', now, () => this.auth.sweep(now));
          await this.sweepStep('email', now, () => this.email.sweep(now));
          // Account deletions stopped after their batch by a storage failure are finished (each one logs a failure).
          await this.sweepStep('deletions', now, () => this.deletion.finishPending());
          // Stored battles past their retention (BATTLES_KEEP, BATTLES_MAX) go; a failure leaves them to the next run.
          await this.sweepStep('battle-archive', now, () => this.sessions.archive.prune(now));
        }
      } finally {
        this.ticking = null;
      }
    })();
    return this.ticking;
  }

  /** When each failing sweep step was last logged (a step that keeps failing is logged once a minute, not every tick). */
  private readonly sweepFailures = new Map<string, number>();

  /** One step of tick(): its failure is logged ('sweep failed' with the step) and does not stop the other steps. */
  private async sweepStep(step: string, now: number, run: () => unknown): Promise<void> {
    try {
      await run();
      this.sweepFailures.delete(step);
    } catch (err) {
      const last = this.sweepFailures.get(step);
      if (last !== undefined && now - last < 60_000 && now >= last) return;
      this.sweepFailures.set(step, now);
      const fields = { step, err, ...(isDocumentDamaged(err) ? { damaged: true } : {}) };
      if (isDocumentDamaged(err)) this.logger.warn('sweep failed', fields);
      else this.logger.error('sweep failed', fields);
    }
  }

  /** Deletes the stored idempotency documents whose retry window ended. */
  private async pruneStoredIdempotency(now: number): Promise<void> {
    const expired: string[] = [];
    for (const [id, stored] of this.storedIdempotency) if (stored.expires <= now) expired.push(id);
    for (const id of expired) {
      this.untrackStored(id);
      this.restoredIdempotency.delete(id);
    }
    await this.deleteIdempotencyDocs(expired);
  }

  private untrackStored(id: string): void {
    const stored = this.storedIdempotency.get(id);
    if (!stored) return;
    this.storedIdempotency.delete(id);
    this.storedIdempotencyBytes -= stored.bytes;
  }

  private async deleteIdempotencyDocs(ids: readonly string[]): Promise<void> {
    for (let i = 0; i < ids.length; i += 500) {
      await this.storage.batch(ids.slice(i, i + 500).map((id) => deleteOp(C.idempotency, id)));
    }
  }

  startSweeper(intervalMs = this.config.sweepIntervalMs): void {
    if (this.sweeper) return;
    this.sweeper = setInterval(() => void this.tick(), intervalMs);
    (this.sweeper as { unref?: () => void }).unref?.();
  }

  stopSweeper(): void {
    if (this.sweeper) clearInterval(this.sweeper);
    this.sweeper = null;
  }

  /**
   * First phase of a graceful shutdown: in-flight requests still complete, but long polls return at once
   * and no background sweeps run any more.
   */
  drain(): void {
    this.stopSweeper();
    this.hub.closeAll();
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.sessions.stop();
    this.stopSweeper();
    if (this.ticking) await this.ticking;
    this.hub.closeAll();
    // E-mails being handed to the provider finish (their audit entries are stored) before the storage closes.
    await this.email.idle();
    await this.flush();
    await this.storage.close();
  }

  // ------------------------------------------------------------------------------------------------------
  // Runtime introspection and state flushing

  /** Cheap counters for /health (no storage access). */
  stats(): BackendStats {
    return { ...this.hub.stats(), rooms: liveCount(this.rooms, 'rooms'), sessions: liveCount(this.sessions, 'sessions') };
  }

  private readonly flushHooks = new Set<FlushHook>();

  /** Registers a hook that persists in-memory state; returns the unregister function. */
  onFlush(hook: FlushHook): () => void {
    this.flushHooks.add(hook);
    return () => this.flushHooks.delete(hook);
  }

  /**
   * Runs the flush hooks (a failing hook is logged and does not stop the others), waits for the idempotency records
   * being written, then for durable storage.
   */
  async flush(): Promise<void> {
    for (const hook of [...this.flushHooks]) {
      try {
        await hook();
      } catch (err) {
        this.logger.error('state flush failed', { err });
      }
    }
    await Promise.allSettled([...this.idempotencyWrites]);
    await this.storage.flush();
  }
}
